<template>
  <main-logo-register />

  <FormCard
    :image-src="content.src"
    :title="content.title"
    :description="content.description"
    :card-style="{ maxWidth: '700px', paddingLeft: '50px', paddingRight: '50px' }"
    :class="{ admin: isAdmin }"
  >
    <div class="content">
      <template v-if="isAdmin && !isDisabledUser">
        <h3>
          Realiza el pago de tu plan para reactivarlo.
        </h3>

        <table aria-hidden="true">
          <tr>
            <td>Fecha de pago:</td>
            <td>{{ formatDate(String(route.query.date_payment)) }}</td>
          </tr>
          <tr>
            <td>Número de slots:</td>
            <td>{{ route.query.slots_invoice }}</td>
          </tr>
          <tr>
            <td>Precio por slot:</td>
            <td>${{ Number(route.query.price) }}</td>
          </tr>
          <tr v-if="Number(route.query.tax) > 0">
            <td>
              IGV (18%) de slots
              <a-tooltip>
                <template #title>
                  Cálculo del 18% de IGV sobre el precio por slot multiplicado por el número de slots. 
                  Es un costo adicional separado.
                </template>
                <InfoCircleOutlined />
              </a-tooltip>
            </td>
            <td>${{ Number(route.query.price) * Number(route.query.slots_invoice) * Number(route.query.tax) }}</td>
          </tr>
          <tr>
            <td><strong>Total a pagar:</strong></td>
            <td>
              <strong>${{ Number(route.query.price) * Number(route.query.slots_invoice) * (Number(route.query.tax) ? 118 : 100)/100}}</strong>
            </td>
          </tr>
        </table>

        <PayUPaymentButton
          name="Pagar plan"
          isExpiredBill
          :amount="Number(route.query.price) * Number(route.query.slots_invoice) * (Number(route.query.tax) ? 118 : 100)/100"
          :type="3"
          @update="$emit('update')"
          @success="handleOpenModal('success')"
          @reject="handleOpenModal('error')"
        />

        <TransactionStateModal
          :open="transactionModal.open"
          :state="transactionModal.state"
          @update:open="updateTransactionModalOpen"
        />

        <span class="text-info">
          Si tienes alguna duda, puedes escribirnos a support@beexcc.com
        </span>
      </template>

      <template v-else>
        <img :src="billingLockImage" alt="lock" />
      </template>
    </div>
  </FormCard>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, reactive } from "vue";
import dayjs, { type Dayjs } from "dayjs";

import { useValidateWorkspace } from "@/app/auth/composables/useValidateWorkspace";

import FormCard from "@/app/auth/components/atoms/FormCard.vue";
import MainLogoRegister from "@/app/shared/components/molecules/MainLogoRegister.vue";
import billingLockImage from "@/app/settings/assets/billing-lock.png";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import TransactionStateModal from "@/app/settings/components/modals/TransactionStateModal.vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

/**
 * Query params
 *
 * slot: string;
 * admin: "True" | "False";
 * date_payment: string;
 * date_payment_finish: string;
 * price: string;
 * slots_invoice: string;
 * tax: string;
 */

const route = useRoute();
const router = useRouter();
const workspaceData = useValidateWorkspace();

const formatDate = (date: string | Dayjs) => dayjs(date).format("DD/MM/YYYY");

const isAdmin = computed(() => route.query.admin === "True");
const isDisabledUser = computed(() => route.query.user === "disabled");

const content = computed(() => ({
  src: isAdmin.value ? billingLockImage : workspaceData.value?.picture,
  title: isDisabledUser.value ?
    `Tu usuario se encuentra inactivo en este equipo de trabajo "${workspaceData.value?.name}"` :
    isAdmin.value
    ? `Tu ambiente de trabajo fue bloqueado el \n ${formatDate(String(route.query.date_payment_finish))} 😥`
    : `El equipo de trabajo de ${workspaceData.value?.name} se encuentra bloqueado temporalmente 😥 `,
  description: isAdmin.value || isDisabledUser.value
    ? ""
    : "Por favor, comuníquese con su administrador ante cualquier duda. ",
}));

const transactionModal = reactive({
  open: false,
  state: "success",
});

const handleOpenModal = (state: string) => {
  transactionModal.state = state;
  // if (transactionModal.state === "success") {
  transactionModal.open = true;
  // }
  // } else {
  //   transactionModal.open = true;
  // }
};

const updateTransactionModalOpen = (value: boolean) => {
  transactionModal.open = value;
  if (!value && transactionModal.state === "success")
    router.push("/login");
};
</script>

<style scoped lang="sass">
.content
  display: flex
  flex-direction: column
  align-items: center

  h3
    text-align: center
    font-size: 24px
    color: $gray-9
    font-weight: 600
    margin: 0
  
  .text-info  
    font-size: 16px
    font-weight: 400
    color: #000000A6
    display: block
    margin-top: 12px
    text-align: center

img
  margin-top: 48px

.admin:deep(.card .caption h2)
  color: $gray-8 !important
  white-space: pre-line

table
  width: 70%
  margin: 45px auto

td
  width: 50%
  padding: 7px 0

tr:not(:last-child)
  border-bottom: 1px solid $text-cuaternary
</style>
